const area = (q) => Math.abs(q.reduce((s, p, i) => { const n = q[(i + 1) % 4]; return s + p[0] * n[1] - n[0] * p[1]; }, 0)) / 2;

// Orders 4 points clockwise starting from top-left.
export const orderQuad = (pts) => {
  const cx = pts.reduce((s, p) => s + p[0], 0) / 4, cy = pts.reduce((s, p) => s + p[1], 0) / 4;
  const s = [...pts].sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
  const start = s.reduce((bi, p, i) => (p[0] + p[1] < s[bi][0] + s[bi][1] ? i : bi), 0);
  return [...s.slice(start), ...s.slice(0, start)];
};

export const defaultQuad = (c) => {
  const mx = c.width * 0.1, my = c.height * 0.12;
  return [[mx, my], [c.width - mx, my], [c.width - mx, c.height - my], [mx, c.height - my]];
};

// Finds the 4 corners of the card: Otsu threshold, largest blob, extreme points (diagonal or axis, whichever encloses more).
export const detectQuad = (canvas) => {
  const W = 360, s = canvas.width / W, H = Math.round(canvas.height / s), n = W * H;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, 0, 0, W, H);
  const px = ctx.getImageData(0, 0, W, H).data;
  const g = new Uint8Array(n), hist = new Float64Array(256);
  for (let i = 0; i < n; i++) { g[i] = (0.299 * px[i * 4] + 0.587 * px[i * 4 + 1] + 0.114 * px[i * 4 + 2]) | 0; hist[g[i]]++; }
  let sum = 0; for (let t = 0; t < 256; t++) sum += t * hist[t];
  let sB = 0, wB = 0, best = 0, th = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t]; if (!wB) continue;
    const wF = n - wB; if (!wF) break;
    sB += t * hist[t];
    const v = wB * wF * (sB / wB - (sum - sB) / wF) ** 2;
    if (v > best) { best = v; th = t; }
  }
  let hi = 0, cnt = 0;
  for (let x = 0; x < W; x++) { cnt += 2; hi += (g[x] > th) + (g[(H - 1) * W + x] > th); }
  for (let y = 0; y < H; y++) { cnt += 2; hi += (g[y * W] > th) + (g[y * W + W - 1] > th); }
  const fgHigh = hi < cnt / 2;
  const lab = new Uint8Array(n), stack = new Int32Array(n);
  let top = null;
  for (let i0 = 0; i0 < n; i0++) {
    if (lab[i0] || (g[i0] > th) !== fgHigh) continue;
    let sp = 0, size = 0; stack[sp++] = i0; lab[i0] = 1;
    const ex = { d1: [1e9], d2: [-1e9], d3: [-1e9], d4: [1e9], l: [1e9], r: [-1e9], t: [1e9], b: [-1e9] };
    const upd = (k, v, x, y, min) => { if (min ? v < ex[k][0] : v > ex[k][0]) ex[k] = [v, x, y]; };
    while (sp) {
      const i = stack[--sp], x = i % W, y = (i / W) | 0; size++;
      upd("d1", x + y, x, y, true); upd("d2", x + y, x, y, false); upd("d3", x - y, x, y, false); upd("d4", x - y, x, y, true);
      upd("l", x, x, y, true); upd("r", x, x, y, false); upd("t", y, x, y, true); upd("b", y, x, y, false);
      const nb = [x > 0 && i - 1, x < W - 1 && i + 1, y > 0 && i - W, y < H - 1 && i + W];
      for (const j of nb) if (j !== false && !lab[j] && (g[j] > th) === fgHigh) { lab[j] = 1; stack[sp++] = j; }
    }
    if (!top || size > top.size) top = { size, ex };
  }
  if (!top || top.size < n * 0.12 || top.size > n * 0.97) return null;
  const pt = (k) => [top.ex[k][1] * s, top.ex[k][2] * s];
  const diag = orderQuad([pt("d1"), pt("d3"), pt("d2"), pt("d4")]);
  const axis = orderQuad([pt("t"), pt("r"), pt("b"), pt("l")]);
  const q = area(axis) > area(diag) * 1.08 ? axis : diag;
  return area(q) > canvas.width * canvas.height * 0.12 ? q : null;
};

const solve = (A, b) => {
  const m = A.map((r, i) => [...r, b[i]]), N = b.length;
  for (let c = 0; c < N; c++) {
    let p = c; for (let r = c + 1; r < N; r++) if (Math.abs(m[r][c]) > Math.abs(m[p][c])) p = r;
    [m[c], m[p]] = [m[p], m[c]];
    for (let r = 0; r < N; r++) if (r !== c) { const f = m[r][c] / m[c][c]; for (let k = c; k <= N; k++) m[r][k] -= f * m[c][k]; }
  }
  return m.map((r, i) => r[N] / r[i]);
};

const homography = (from, to) => {
  const A = [], b = [];
  from.forEach(([x, y], i) => {
    const [X, Y] = to[i];
    A.push([x, y, 1, 0, 0, 0, -X * x, -X * y]); b.push(X);
    A.push([0, 0, 0, x, y, 1, -Y * x, -Y * y]); b.push(Y);
  });
  return [...solve(A, b), 1];
};

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

// Output size: fixed card ratio when given (portrait if the card is held upright), otherwise the measured shape.
export const outSize = (q, long, ratio) => {
  const w = (dist(q[0], q[1]) + dist(q[3], q[2])) / 2, h = (dist(q[0], q[3]) + dist(q[1], q[2])) / 2;
  const r = ratio ? (w >= h ? ratio : 1 / ratio) : w / h;
  return r >= 1 ? [long, Math.round(long / r)] : [Math.round(long * r), long];
};

// Perspective-corrects the quad region into a flat outW x outH canvas (bilinear sampling).
export const warp = (canvas, q, outW, outH) => {
  const sw = canvas.width, sh = canvas.height;
  const src = canvas.getContext("2d", { willReadFrequently: true }).getImageData(0, 0, sw, sh).data;
  const out = document.createElement("canvas"); out.width = outW; out.height = outH;
  const octx = out.getContext("2d"); const img = octx.createImageData(outW, outH); const d = img.data;
  const h = homography([[0, 0], [outW, 0], [outW, outH], [0, outH]], q);
  for (let y = 0; y < outH; y++) for (let x = 0; x < outW; x++) {
    const u = x + 0.5, v = y + 0.5, z = h[6] * u + h[7] * v + 1;
    const sx = Math.min(sw - 1.001, Math.max(0, (h[0] * u + h[1] * v + h[2]) / z - 0.5));
    const sy = Math.min(sh - 1.001, Math.max(0, (h[3] * u + h[4] * v + h[5]) / z - 0.5));
    const x0 = sx | 0, y0 = sy | 0, fx = sx - x0, fy = sy - y0, i00 = (y0 * sw + x0) * 4, i10 = i00 + 4, i01 = i00 + sw * 4, i11 = i01 + 4, o = (y * outW + x) * 4;
    for (let k = 0; k < 3; k++) d[o + k] = (src[i00 + k] * (1 - fx) + src[i10 + k] * fx) * (1 - fy) + (src[i01 + k] * (1 - fx) + src[i11 + k] * fx) * fy;
    d[o + 3] = 255;
  }
  octx.putImageData(img, 0, 0);
  return out;
};
